<template>
  <ViewContainer
    data-tourid="routine-execution"
    :title="routine.name"
    :key="lastFetchTime"
  >
    <template v-slot:prepend>
      <v-btn
        class="me-2"
        color="blue"
        icon
        text
        :ripple="false"
        @click="handleBack"
      >
        <v-icon>mdi-chevron-left</v-icon>
      </v-btn>
    </template>

    <template v-if="permissions.edit" slot="header-right">
      <v-btn
        class="me-1"
        color="blue-grey darken-2"
        icon
        small
        @click="showRoutineEditDialog = true"
      >
        <v-icon size="16"> mdi-pencil-outline </v-icon>
      </v-btn>
    </template>

    <template v-slot:title-append>
      <v-tooltip v-if="routine.isApproved" bottom>
        <template v-slot:activator="{ on, attrs }">
          <v-icon v-bind="attrs" v-on="on" color="primary" size="22">
            mdi-checkbox-marked-circle
          </v-icon>
        </template>
        <span>{{
          $t('routines.approval.total_approvals', {
            count: routine.totalApprovals,
          })
        }}</span>
      </v-tooltip>
    </template>

    <Alert
      v-if="approvalAlertData"
      data-testid="routineExecutionView-alert-approval"
      secondaryButtonColor="danger"
      textClass="body-2 py-2"
      :vuetifyProps="{
        color: approvalAlertData.color,
        icon: 'mdi-alert-circle',
      }"
      :buttonPlain="false"
      :buttonText="approvalAlertData.buttonText"
      :secondaryButtonText="rejectButtonText"
      :text="approvalAlertData.text"
      @buttonClick="startUserApproval"
      @secondaryButtonClick="startUserRejection"
    />

    <Alert
      v-if="routine.status !== 'completed' && permissions.complete"
      data-testid="routineExecutionView-alert-complete"
      textClass="body-2"
      :buttonPlain="false"
      :vuetifyProps="{
        color: 'primary',
        icon: 'mdi-text-box-check-outline',
      }"
      :buttonText="$t('routines.checklist_save_button')"
      :text="$t('routines.once_you_have_finished')"
      @buttonClick="handleCompleteRoutine"
    />

    <v-container>
      <v-row class="mt-0 mb-8">
        <v-col cols="12" class="pa-0 ma-0">
          <v-card class="pa-0">
            <v-tabs
              v-if="activeTab !== null"
              :value="activeTab"
              slider-size="4"
              :grow="false"
              height="72"
              @change="handleTabChange"
            >
              <div
                class="d-flex justify-space-between align-center"
                style="width: 100%"
              >
                <div class="d-flex h-100">
                  <v-tab
                    v-show="routine.checklist"
                    active-class="active-tab font-weight-bold"
                    >{{ $t('checklist.checklist_one') }}</v-tab
                  >
                  <v-tab active-class="active-tab font-weight-bold">{{
                    $t('routines.finding_other')
                  }}</v-tab>

                  <v-tab active-class="active-tab font-weight-bold">{{
                    $t('routines.approval.approvals_tab_title')
                  }}</v-tab>

                  <v-tab active-class="active-tab font-weight-bold">{{
                    $t('common.about')
                  }}</v-tab>
                </div>

                <div class="d-flex align-center h-100 pe-4">
                  <div class="d-flex flex-column align-end me-2">
                    <Status v-if="routine.status" :entity="routine" />

                    <p
                      v-if="isCompleted"
                      class="text-caption grey--text mt-1 mb-0"
                    >
                      {{
                        $t('routines.time_to_complete', {
                          time: timeToComplete,
                        })
                      }}
                    </p>
                  </div>

                  <Button
                    data-testid="routineExecutionView-button-print"
                    plain
                    style="opacity: 1"
                    color="primary"
                    class="px-3 py-2"
                    icon="mdi-printer"
                    :text="$t('common.button.print')"
                    @click="trackPrintRoutine({ type: 'Print routine' })"
                    :to="{
                      name: 'routinePrintView',
                      params: { routineExecutionId: $route.params.routineId },
                    }"
                  />

                  <ExecutionMenu
                    :canDelete="permissions.delete"
                    @delete="showDeleteExecutionDialog = true"
                    @showHistory="isHistoryShown = true"
                  />
                </div>
              </div>
            </v-tabs>

            <v-divider />

            <div class="pa-6">
              <v-window v-model="activeTab">
                <v-window-item :key="tabs.CHECKLIST" :value="tabs.CHECKLIST">
                  <ChecklistTab
                    data-testid="routineExecutionView-container-checklistTab"
                    ref="checklistTab"
                    :routine="routine"
                    :routineDefinition="routineDefinition"
                    :canUpdateRoutine="permissions.complete"
                    :findings="findings"
                    :tableOptions="tableOptions"
                    :isLoading="isLoading"
                    :hasChecklistChanged="hasChecklistChanged"
                    @checklistEditChange="isEditingChecklist = $event"
                    @goToFindings="handleTabChange(tabs.FINDINGS)"
                    @updateRoutine="updateRoutineExecution"
                    @openAddressFindingDrawer="handleOpenAddressFindingDrawer"
                    @openEditFindingDrawer="handleOpenEditFindingDrawer"
                    @getFindings="getRoutineFindings"
                    @openDeleteFindingModal="handleDeleteClick"
                    @checklistHasChanged="checklistChanged($event)"
                    @completeRoutine="requestRoutineCompletion"
                  />
                </v-window-item>

                <v-window-item :key="tabs.FINDINGS" :value="tabs.FINDINGS">
                  <FindingList
                    :routine="routine"
                    :routineDefinition="routineDefinition"
                    :findings="findings"
                    :tableOptions="tableOptions"
                    :isLoading="isLoading"
                    :selectedFindings="selectedFindings"
                    @openAddressFindingDrawer="handleOpenAddressFindingDrawer"
                    @openEditFindingDrawer="handleOpenEditFindingDrawer"
                    @getFindings="getRoutineFindings"
                    @openDeleteFindingModal="handleDeleteClick"
                    @addAssignees="showBulkAddAssigneesModal = true"
                    @editDueDate="showBulkEditDueDateModal = true"
                    @delete="handleBulkDeleteClick"
                    @setSelectedFindings="selectedFindings = $event"
                  />
                </v-window-item>

                <v-window-item :key="tabs.APPROVALS" :value="tabs.APPROVALS">
                  <ApprovalsTab
                    :routine="routine"
                    :routineDefinition="routineDefinition"
                    :approvals="approvals"
                    :isLoadingApprovals="isLoadingApprovals"
                  />
                </v-window-item>

                <v-window-item :key="tabs.ABOUT" :value="tabs.ABOUT">
                  <AboutTab
                    :routine="routine"
                    :loading="isLoading"
                    :canEditRoutine="permissions.edit"
                    @saved="fetchRoutine"
                    @updateRoutine="updateRoutineExecution"
                  />
                </v-window-item>
              </v-window>
            </div>
          </v-card>
        </v-col>
      </v-row>

      <BulkAddAssigneesModal
        v-if="showBulkAddAssigneesModal"
        :selectedTeamId="selectedTeam"
        :siteId="routine.site"
        :selectedFindings="selectedFindings"
        @close="closeBulkModal"
        @success="success"
      />

      <BulkEditDueDateModal
        v-if="showBulkEditDueDateModal"
        :siteId="routine.site"
        :selectedFindings="selectedFindings"
        @close="closeBulkModal"
        @success="success"
      />

      <FindingDrawer
        v-if="openAddressFindingDrawer"
        :visible="openAddressFindingDrawer"
        :findingId="selectedFindingId"
        @close="onAddressFindingDrawerClose"
        @deleted="drawerCloseAndUpdate('ADDRESS_DRAWER')"
      />

      <EditFindingDrawer
        v-if="openEditFindingDrawer"
        :visible="openEditFindingDrawer"
        :findingId="selectedFindingId"
        @close="drawerCloseAndUpdate('EDIT_DRAWER')"
      />

      <BulkDeleteConfirmationModal
        :show="showDeleteDialog"
        :findingsToDeleteIds="findingsToDeleteIds"
        :findingToDelete="findingToDelete"
        @cancel="showDeleteDialog = false"
        @success="deleteSuccess"
      />

      <DeleteConfirmationModal
        :show="showDeleteExecutionDialog"
        :isLoading="isDeleting"
        :title="$t('routines.delete_excution_title')"
        :text="deleteText"
        @cancel="showDeleteExecutionDialog = false"
        @delete="deleteRoutine"
      />

      <ConfirmationModal
        modalTestID="routineExecutionView-modal-completeConfirmation"
        confirmTestID="routineExecutionView-button-confirmComplete"
        cancelTestID="routineExecutionView-button-cancelComplete"
        :show="showCompleteConfirmation"
        :isLoading="isLoading"
        :title="$t('routines.complete_warning_title')"
        :text="$t('routines.complete_warning_description')"
        :confirmText="$t('routines.checklist_save_button')"
        @cancel="closeCompleteConfirmation"
        @close="closeCompleteConfirmation"
        @confirm="confirmRoutineCompletion"
      />

      <HistoryModal
        v-if="isHistoryShown"
        :show="isHistoryShown"
        :routineId="routine.id"
        @close="isHistoryShown = false"
      />

      <RoutineStartModal
        v-if="showRoutineEditDialog"
        isEditing
        :open="showRoutineEditDialog"
        :routine="routine"
        @cancel="showRoutineEditDialog = false"
        @saved="handleRoutineSaveSuccess"
      />

      <RoutineApprovalDialog
        v-if="showApprovalDialog"
        data-testid="routineExecutionView-dialog-approveRequest"
        shouldApproveRequest
        :approval="pendingApprovalRequestItem"
        :routine="routine"
        :routineId="routine.id"
        :show="showApprovalDialog"
        @approved="handleApprovalDialogApproved"
        @close="showApprovalDialog = false"
        @updated="handleApprovalDialogApproved"
      />

      <RejectApprovalDialog
        v-if="showRejectDialog"
        data-testid="routineExecutionView-dialog-rejectRequest"
        :approval="pendingApprovalRequestItem"
        :routineId="routine.id"
        :show="showRejectDialog"
        @close="showRejectDialog = false"
        @rejected="handleApprovalDialogRejected"
      />
    </v-container>
  </ViewContainer>
</template>

<script>
import _ from 'lodash'
import { mapGetters } from 'vuex'
import moment from 'moment'
import { utils } from 'frontend-js-shared'

import mixin from '@/mixins/mixin.vue'
import routineApprovalPermissions from '@/mixins/routineApprovalPermissions.vue'
import routinePermissions from '@/mixins/routinePermissions.vue'
import trackerMixin from '@/mixins/tracking/trackerMixin.vue'
import eventHub, { HUB_EVENTS } from '@/lib/event-hub'
import api from '@/api'

import FindingDrawer from '@/components/FindingDrawer/FindingDrawer.vue'
import EditFindingDrawer from '@/components/FindingEditDrawer/FindingEditDrawer.vue'
import BulkEditDueDateModal from '@/components/BulkEditDueDateModal.vue'
import BulkAddAssigneesModal from '@/components/BulkAddAssigneesModal.vue'
import BulkDeleteConfirmationModal from '@/components/BulkDeleteConfirmationModal.vue'
import RoutineStartModal from '@/components/RoutineStartModal.vue'
import Button from '@/components/UI/Button'
import ViewContainer from '@/components/Core/Layout/ViewContainer.vue'
import ConfirmationModal from '@/components/Modals/ConfirmationModal.vue'
import DeleteConfirmationModal from '@/components/Modals/DeleteConfirmationModal.vue'

import Alert from '@/components/Alert.vue'
import { buildAccountFullName } from '@/components/Checklists/ChecklistReply/signatureUtils'
import RoutineApprovalDialog from '@/components/Routines/RoutineApproval/RoutineApprovalDialog.vue'
import RejectApprovalDialog from '@/components/Routines/RoutineApproval/RejectApprovalDialog.vue'

import ExecutionMenu from './ExecutionMenu.vue'
import Status from '@/components/Core/StatusChip.vue'
import ChecklistTab from './ChecklistTab.vue'
import FindingList from './FindingList.vue'
import AboutTab from './AboutTab.vue'
import HistoryModal from './HistoryModal.vue'
import ApprovalsTab from './Approvals/ApprovalsTab.vue'
import { ROUTINE_STATUS } from './Approvals/constants'

const DEFAULT_SIZE = 10

const sortOrder = {
  ASCENDING: 'asc',
  DESCENDING: 'desc',
}

const TAB_NAMES = Object.freeze({
  CHECKLIST: 'checklist',
  FINDINGS: 'findings',
  APPROVALS: 'approvals',
  ABOUT: 'about',
})

const TAB_NAME_TO_INDEX = Object.freeze({
  [TAB_NAMES.CHECKLIST]: 0,
  [TAB_NAMES.FINDINGS]: 1,
  [TAB_NAMES.APPROVALS]: 2,
  [TAB_NAMES.ABOUT]: 3,
})

export default {
  name: 'RoutineExecutionView',

  mixins: [mixin, routinePermissions, routineApprovalPermissions, trackerMixin],

  components: {
    AboutTab,
    Alert,
    ApprovalsTab,
    BulkAddAssigneesModal,
    BulkDeleteConfirmationModal,
    BulkEditDueDateModal,
    Button,
    ChecklistTab,
    ConfirmationModal,
    DeleteConfirmationModal,
    EditFindingDrawer,
    ExecutionMenu,
    FindingDrawer,
    FindingList,
    HistoryModal,
    RejectApprovalDialog,
    RoutineApprovalDialog,
    RoutineStartModal,
    Status,
    ViewContainer,
  },

  data() {
    return {
      lastFetchTime: Date.now(),
      activeTab: null,
      DEFAULT_SIZE,
      findings: [],
      findingsToDeleteIds: [],
      findingToDelete: {},
      hasChecklistChanged: false,
      isDeleting: false,
      pendingRoutineCompletionData: null,
      showRoutineEditDialog: false,
      isEditingChecklist: false,
      isHistoryShown: false,
      isLoading: false,
      openAddressFindingDrawer: false,
      openEditFindingDrawer: false,
      prevRoute: null,
      routine: {},
      routineDefinition: {},
      selectedFindingId: '',
      selectedFindings: [],
      showBulkAddAssigneesModal: false,
      showBulkEditDueDateModal: false,
      showCompleteConfirmation: false,
      showDeleteDialog: false,
      showDeleteExecutionDialog: false,
      tableOptions: {
        page: 1,
        itemsPerPage: DEFAULT_SIZE,
        itemsPerPageOptions: [10, 20, 50, 100],
      },
      approvals: [],
      showApprovalDialog: false,
      showRejectDialog: false,
      isLoadingApprovals: false,
    }
  },

  beforeRouteEnter(_to, from, next) {
    next(vm => {
      vm.prevRoute = from
    })
  },

  beforeRouteLeave(_to, _from, next) {
    if (!this.hasChecklistChanged) return next()
    if (window.confirm(this.$t('checklist.unsaved_answers_warning'))) {
      next()
    } else {
      next(false)
    }
  },

  computed: {
    ...mapGetters(['organizationId', 'account']),

    tabs() {
      return {
        CHECKLIST: 0,
        FINDINGS: 1,
        APPROVALS: 2,
        ABOUT: 3,
      }
    },

    permissions() {
      return this.getRoutinePermissions({
        routine: this.routine,
        routineDefinition: this.routineDefinition,
      })
    },

    selectedTeam() {
      return this.routine?.selectedTeam?.id
    },

    isCompleted() {
      return this.routine?.status === 'completed'
    },

    rejectButtonText() {
      if (!this.approvalPermissions?.reject) {
        return ''
      }

      if (!this.pendingApprovalRequestItem) {
        return ''
      }

      return this.$t('common.button.reject')
    },

    approvalPermissions() {
      const pendingItem = this.pendingApprovalRequestItem

      if (!pendingItem) {
        return {}
      }

      return this.getRoutineApprovalPermissions({
        routineApproval: pendingItem,
        routine: this.routine,
      })
    },

    pendingApprovalRequestItem() {
      return (
        this.approvals.find(
          a =>
            utils.getEntityId(a?.approver) === this.account.id &&
            a.status === 'requested'
        ) || null
      )
    },

    approvalAlertData() {
      const pendingItem = this.pendingApprovalRequestItem

      if (!pendingItem) {
        return null
      }

      const name = buildAccountFullName(pendingItem?.createdBy) || '-'
      const date = utils.dates.formatDate(
        pendingItem?.createdAt,
        'MMM D YYYY, h:mm A'
      )

      return {
        color: 'primary',
        text: this.$t('routines.approval.request_pending_alert', {
          name,
          date,
        }),
        buttonText: this.$t('common.approve'),
      }
    },

    timeToComplete() {
      if (!this.isCompleted) return null

      const { createdAt, completedAt } = this.routine
      const milliseconds = moment(completedAt).diff(createdAt, 'milliseconds')

      return moment.duration(milliseconds).humanize({ d: Infinity }) // The object parameter passed to the "humanize" function is a "thresholds" config (https://momentjs.com/docs/#/durations/humanize/) that prevents duration from being converted to a unit bigger than days.
    },

    deleteText() {
      return this.findings?.length
        ? this.$t('routines.delete_execution_with_findings')
        : this.$t('routines.delete_execution_confirmation')
    },

    isRoutineApproved() {
      return this.routine?.isApproved
    },

    shouldWarnBeforeComplete() {
      return Boolean(this.routine?.checklist?.scoringType)
    },
  },

  async created() {
    await this.fetchRoutine()
    await this.fetchRoutineDefinition()

    const { index, shouldCleanQuery } = this.getInitialActiveTab(
      this.$route.query?.tab
    )
    this.activeTab = index

    if (shouldCleanQuery) {
      this.removeTabFromQuery()
    }

    await this.fetchApprovals()
  },

  mounted() {
    eventHub.$on(
      HUB_EVENTS.ROUTINE_APPROVAL.UPDATED,
      this.handleRoutineApprovalUpdated
    )
  },

  beforeDestroy() {
    eventHub.$off(
      HUB_EVENTS.ROUTINE_APPROVAL.UPDATED,
      this.handleRoutineApprovalUpdated
    )
  },

  methods: {
    async handleRoutineApprovalUpdated(payload) {
      if (payload?.routineId && payload.routineId !== this.routine.id) {
        return
      }

      await this.fetchApprovals()
    },

    getInitialActiveTab(tabName) {
      const defaultIndex = this.routine.checklist
        ? this.tabs.CHECKLIST
        : this.tabs.FINDINGS

      if (_.isNil(tabName) || tabName === '') {
        return { index: defaultIndex, shouldCleanQuery: false }
      }

      const requestedIndex = TAB_NAME_TO_INDEX[tabName]

      if (_.isUndefined(requestedIndex)) {
        return { index: defaultIndex, shouldCleanQuery: true }
      }

      if (tabName === TAB_NAMES.CHECKLIST && !this.routine.checklist) {
        return {
          index: this.tabs.FINDINGS,
          shouldCleanQuery: true,
        }
      }

      return { index: requestedIndex, shouldCleanQuery: false }
    },

    removeTabFromQuery() {
      const nextQuery = { ...this.$route.query }
      delete nextQuery.tab

      this.$router.replace({
        path: this.$route.fullPath,
        query: nextQuery,
      })
    },

    handleTabChange(newIndex) {
      if (newIndex === this.activeTab) return
      this.activeTab = newIndex
      this.syncTabInUrl(newIndex)
    },

    syncTabInUrl(index) {
      if (_.isNil(index)) return

      const tabName = Object.keys(TAB_NAME_TO_INDEX).find(
        key => TAB_NAME_TO_INDEX[key] === index
      )

      if (
        !tabName ||
        (tabName === TAB_NAMES.CHECKLIST && !this.routine.checklist)
      ) {
        return
      }

      const nextQuery = { ...this.$route.query, tab: tabName }

      this.$router.replace({
        path: this.$route.fullPath,
        query: nextQuery,
      })
    },

    getExecutionRelations() {
      const relations = [
        'checklist',
        'checklist.items.referenceMedia',
        'checklistResponse',
        'checklistResponse.items.referenceMedia',
        'checklistResponse.items.responseImages',
        'createdBy',
        'createdBy.photo',
        'media',
        'site',
      ]

      relations.splice(
        5,
        0,
        'checklistResponse.items.signer',
        'checklistResponse.items.signer.photo'
      )

      return relations
    },

    goBackOrNavigateTo(fallbackLocation) {
      if (this.prevRoute?.name) {
        this.$router.back()
      } else {
        this.navigateTo(fallbackLocation)
      }
    },

    handleBack() {
      this.goBackOrNavigateTo({
        name: 'routineDefinitionView',
        params: { routineId: this.routine.routineDefinition },
      })
    },

    handleRoutineSaveSuccess() {
      this.showRoutineEditDialog = false
      this.fetchRoutine()
    },

    async fetchRoutine() {
      try {
        this.isLoading = true
        const response = await api.routine.getExecutionById(
          this.organizationId,
          this.$route.params.routineId,
          {
            relations: this.getExecutionRelations(),
          }
        )

        this.routine = response?.data ?? {}
        this.lastFetchTime = Date.now()
      } catch (error) {
        this.checkNotFoundError(error)
        this.handleRequestError(error)
      } finally {
        this.isLoading = false
      }
    },

    async fetchRoutineDefinition() {
      try {
        this.isLoading = true

        const { data: routineDefinition } = await api.routine.getById(
          this.organizationId,
          this.routine.routineDefinition
        )

        this.routineDefinition = routineDefinition
      } catch (err) {
        this.handleRequestError(err)
      } finally {
        this.isLoading = false
      }
    },

    async updateRoutineExecution(data) {
      try {
        this.isLoading = true
        await api.routine.updateRoutineExecution(
          this.organizationId,
          this.routine.id,
          {
            ...data,
            notes: data?.notes ?? this.routine.notes,
            media:
              data?.media?.map?.(media => media.id) ??
              this.routine?.media?.map?.(media => media.id),
            status: data?.status ?? this.routine.status,
          }
        )

        await this.fetchRoutine()

        this.showSuccess(this.$t('routines.routine_updated_successfully'))

        if (data?.status === 'completed') {
          this.trackCompleteRoutine({
            hasNotes: Boolean(this.routine?.notes),
            hasMedia: Boolean(this.routine?.media?.length),
            hasChecklist: Boolean(this.routine.checklist),
            hasChecklistResponse: Boolean(data?.checklistResponse),
            name: this.routine.name,
          })
        } else {
          this.trackSavedRoutine({
            hasNotes: Boolean(this.routine?.notes),
            hasMedia: Boolean(this.routine?.media?.length),
            name: this.routine.name,
          })
        }
      } catch (err) {
        this.handleRequestError(err)
      } finally {
        this.isLoading = false
      }
    },

    handleOpenAddressFindingDrawer(findingId) {
      this.selectedFindingId = findingId
      this.openAddressFindingDrawer = true
    },

    handleOpenEditFindingDrawer(findingId) {
      this.selectedFindingId = findingId
      this.openEditFindingDrawer = true
    },

    onAddressFindingDrawerClose() {
      this.selectedFindingId = null
      this.openAddressFindingDrawer = false
      this.getRoutineFindings(this.tableOptions)
    },

    drawerCloseAndUpdate(drawer) {
      this.selectedFindingId = null
      if (drawer === 'ADDRESS_DRAWER') {
        this.openAddressFindingDrawer = false
      }
      if (drawer === 'EDIT_DRAWER') {
        this.openEditFindingDrawer = false
      }

      this.getRoutineFindings(this.tableOptions)
    },

    onEditFindingDrawerClose() {
      this.selectedFindingId = null
      this.openEditFindingDrawer = false
      this.getRoutineFindings(this.tableOptions)
    },

    async handleCompleteRoutine() {
      if (this.routine.checklist) {
        this.activeTab = this.tabs.CHECKLIST
        this.$nextTick(() => {
          this.$refs.checklistTab?.attemptCompleteFromAlert()
        })
        return
      }

      await this.requestRoutineCompletion({ status: 'completed' })
    },

    async requestRoutineCompletion(data) {
      const routineCompletionData = data ?? { status: 'completed' }
      this.pendingRoutineCompletionData = routineCompletionData

      if (
        routineCompletionData?.skipConfirmation ||
        !this.shouldWarnBeforeComplete
      ) {
        await this.confirmRoutineCompletion()
        return
      }

      this.showCompleteConfirmation = true
    },

    closeCompleteConfirmation() {
      this.pendingRoutineCompletionData = null
      this.showCompleteConfirmation = false
    },

    async confirmRoutineCompletion() {
      const completionData = this.pendingRoutineCompletionData ?? {
        status: 'completed',
      }
      const routineUpdateData = _.omit(completionData, ['skipConfirmation'])

      this.showCompleteConfirmation = false
      this.pendingRoutineCompletionData = null
      await this.updateRoutineExecution(routineUpdateData)
    },

    async getRoutineFindings(options) {
      try {
        this.isLoading = true
        const response = await api.findings.getAll({
          organizationId: this.organizationId,
          filters: {
            routine: this.routine.id,
          },
          limit: options.itemsPerPage || this.DEFAULT_SIZE,
          page: options.page,
          sort: {
            by: options.sortBy,
            order: options.sortDesc
              ? sortOrder.DESCENDING
              : sortOrder.ASCENDING,
          },
        })

        this.findings = response?.data?.results ?? []

        this.tableOptions.totalRecordsInData =
          response?.data?.totalRecordsInData ?? 0
        this.tableOptions.page = options.page
        this.tableOptions.itemsPerPage = options.itemsPerPage
        this.selectedFindings = []
      } catch (err) {
        this.handleRequestError(err)
      } finally {
        this.isLoading = false
      }
    },

    handleBulkDeleteClick() {
      if (this.selectedFindings.length === 1)
        this.findingToDelete = this.selectedFindings[0]
      this.findingsToDeleteIds = this.selectedFindings.map(
        element => element._id
      )
      this.showDeleteDialog = true
    },

    handleDeleteClick(finding) {
      this.findingToDelete = finding
      this.findingsToDeleteIds = [finding._id]
      this.showDeleteDialog = true
    },

    closeBulkModal() {
      this.showBulkEditDueDateModal = false
      this.showBulkAddAssigneesModal = false
    },

    success() {
      this.selectedFindings = []
      this.showBulkEditDueDateModal = false
      this.showBulkAddAssigneesModal = false
      this.getRoutineFindings(this.tableOptions)
    },

    deleteSuccess() {
      this.selectedFindings = []
      this.findingToDelete = {}
      this.findingsToDeleteIds = []
      this.showDeleteDialog = false
      this.getRoutineFindings(this.tableOptions)
    },

    async deleteRoutine() {
      try {
        this.hasChecklistChanged = false
        this.isDeleting = true
        await api.routine.deleteRoutineExecutions(
          this.organizationId,
          this.routine.id
        )
        this.isEditingChecklist = false
        this.goBackOrNavigateTo({ name: 'routineDefinitions' })
      } catch (err) {
        this.handleRequestError(err)
      } finally {
        this.isDeleting = false
        this.showDeleteExecutionDialog = false
      }
    },

    checklistChanged(val) {
      this.hasChecklistChanged = val
    },

    async fetchApprovals() {
      try {
        if (this.routine?.status !== ROUTINE_STATUS.COMPLETED) return

        this.isLoadingApprovals = true

        const { data } = await api.routine.getRoutineApprovals(
          this.organizationId,
          this.routine.id,
          {
            relations: [
              'createdBy',
              'createdBy.photo',
              'approver',
              'approver.photo',
            ],
          }
        )

        this.approvals = data?.results || []
      } catch (error) {
        this.approvals = []
        this.handleRequestError(error)
      } finally {
        this.isLoadingApprovals = false
      }
    },

    startUserApproval() {
      this.showApprovalDialog = true
    },

    startUserRejection() {
      if (!this.rejectButtonText) {
        return
      }

      this.showRejectDialog = true
    },

    async handleApprovalDialogApproved() {
      await this.fetchApprovals()
    },

    async handleApprovalDialogRejected() {
      await this.fetchApprovals()
    },
  },
}
</script>

<style lang="scss" scoped>
.active-tab {
  color: map-get($grey, darken-4);
}
</style>
